import { SelectOptionType } from '@/components/content/SelectBoxTypes'

/** 입고번호 필터 상한 — BE `@Size(max = 200)` 과 같은 값이다. */
export const ORD_OPTION_NUMBER_LIMIT = 200

const toOptions = (values: string[]): SelectOptionType<string>[] => values.map((v) => ({ value: v, label: v }))

/**
 * 입고유형 옵션 — moms-be `OrderType` enum 의 displayName 그대로다.
 *
 * 핸드오프 데모는 폐기·테스트1·증정·재고이동을 포함한 12종을 보여 주지만 그 넷은 2.0 enum 에 없다.
 * `ErpInboundSearchService.buildCondition` 이 `OrderType.fromCode` 로 변환하다 실패하면 400 을 주므로
 * 데모 목록을 그대로 옮기면 조회가 깨진다. 데이터 계약은 BE 가 정본이라 enum 을 따랐다.
 */
export const ORDER_TYPE_OPTIONS = toOptions(['일반', 'PLUS', '사내', '사내(해외)', '명품', '반품', '해외', '해외(현지)', '교환', '기타'])

/**
 * 배송유형 옵션 — moms-be `TransportType` enum 의 code 그대로다.
 *
 * 데모의 택배-특송·수출·긴급(퀵)·기타는 enum 에 없고(400), 반대로 택배-현지는 데모에 없다.
 * `TransportType.isValidCode` 검증에 걸리므로 enum 을 정본으로 삼는다.
 */
export const TRANSPORT_TYPE_OPTIONS = toOptions([
  '새벽배송',
  '당일배송',
  '익일배송',
  '택배',
  '택배-명품',
  '택배-단포',
  '택배-현지',
  '용차',
  '특송',
])

/** 엑셀 다운로드 상세사유 최소 길이 — 1.0 RemarkPop 검증과 같다. */
export const REASON_DETAIL_MIN_LENGTH = 5

/**
 * 화면 문구. 확인창 제목·본문(confirmTitle·saveContent·confirmContent·deleteContent)과 noSelection 은
 * 컨덕터 생성본이 그리게 되면서 이 파일에서는 더 부르지 않는다 — 옛 `components/` 가 아직 쓰고 있어
 * 개발팀 정리 몫으로 남긴다(사용자 결정 2026-09-18).
 */
export const MESSAGES = {
  /** 엑셀 사유 모달 검증 — 사유코드 선택과 상세사유 최소 길이. */
  downloadValidationFailed: `사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`,
  noSelection: '선택된 데이터가 존재하지 않습니다.',
  noShipperCode: '화주를 확인할 수 없습니다. 화주명을 선택한 뒤 다시 조회해 주세요.',
  actionInProgress: '다른 작업이 진행 중입니다. 끝난 뒤 다시 시도해 주세요.',
  // 확정은 되돌릴 화면 경로가 없다. 대상이 아닌 행이 섞이면 조용히 걸러 내지 않고 전체를 막는다 —
  // 걸러 내면 사용자는 선택한 만큼 확정된 줄 알고, 무엇이 빠졌는지 화면에서 알 길이 없다.
  unconfirmableRows: (count: number) =>
    `입고 지시 정보를 찾을 수 없는 행이 ${count}건 포함돼 있습니다. 해당 행을 선택 해제한 뒤 다시 시도해 주세요.`,
  confirmTitle: '알림',
  saveContent: '선택한 항목을 저장하시겠습니까?',
  // 확정은 WMS 로 ASN 을 밀어 넣고 되돌릴 화면 경로가 없다. 목록 검색으로 화면에 안 보이는
  // 선택 행도 그대로 전송 대상이라, 건수를 밝히지 않으면 사용자가 몇 건이 나가는지 확인 시점에
  // 알 길이 없다(handleConfirm 이 이미 셈한 confirmIds.length 를 그대로 받는다).
  confirmContent: (count: number) => `선택한 ${count}건을 입고확정 하시겠습니까?`,
  confirmSucceeded: (count: number) => `${count}건 입고확정되었습니다.`,
  confirmFailed: '입고확정 중 오류가 발생했습니다.',
  /**
   * 삭제 확인 문구. 삭제 범위는 선택한 행이 아니라 입고번호(DELVNO)다 — BE 가
   * `(화주코드, ORD_OPT_NO)` 로 찾아 그 입고번호에 딸린 입고 예정을 통째로 지운다.
   * 1.0 도 같은 범위지만 확인 대화 자체가 없어 범위를 말한 적이 없다. 여기서 "선택한 항목" 이라고
   * 하면 없던 오해를 만들므로, 실제로 지워지는 단위를 문구에 드러낸다.
   */
  deleteContent: (delvNos: string[]) => {
    // 두 줄 문구(사용자 확정 2026-09-10). 입고번호가 여럿이면 첫 번호 + "외 n건"(n = 나머지 입고번호 수 — 삭제 단위가 입고번호라 행 수가 아니다).
    const [first = '', ...others] = delvNos
    const target = others.length ? `${first} 외 ${others.length}건` : first
    return `입고 번호 ${target}에 연결된 입고 내역이 모두 삭제되어 선택하지 않은 건도 함께 삭제됩니다.\n삭제하시겠어요?`
  },
  deleteSucceeded: (count: number) => `${count}건 삭제되었습니다.`,
  deleteFailed: '삭제 중 오류가 발생했습니다.',
  excelNoRows: '다운로드할 데이터가 없습니다.',
  ordOptionNumberLimitExceeded: `입고번호는 최대 ${ORD_OPTION_NUMBER_LIMIT}개까지 입력할 수 있습니다.`,
  saveSucceeded: (count: number) => `${count}건 저장되었습니다.`,
  /**
   * 저장 부분 실패. BE 는 요청 처리 중 예외가 나도 HTTP 200 을 주고 `T_RESULT` 행마다 실패
   * 코드(MSGTYP≠'S')를 싣는다 — 400 원문을 그대로 보여 주지 않고, 실패 건수와 서버 메시지를
   * 함께 알린다.
   */
  saveFailed: (failedCount: number, totalCount: number, message: string) =>
    `${totalCount}건 중 ${failedCount}건 저장에 실패했습니다. ${message}`,
  saveRequestFailed: '저장 중 오류가 발생했습니다.',
} as const
